import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { apiClient } from "@/lib/api";
import { getPlanColor, withAlpha } from "@/lib/plan-colors";
import { formatCurrency, formatNumber } from "@/lib/utils";
import { Plan } from "@/types";
import { Check } from "lucide-react";
import { useState } from "react";
import type { CSSProperties } from "react";

type PlanWithCurrent = Plan & { current?: boolean };

type PlansProps = {
  plans: PlanWithCurrent[];
  refetchCredits?: () => Promise<void>;
  refetchSubscription?: () => Promise<void>;
};

// Helper functions
const getPlanCredits = (plan: Plan): number => {
  if (plan.credits && plan.credits > 0) {
    return plan.credits;
  }

  if (plan.metadata?.credits) {
    const credits = parseInt(plan.metadata.credits);
    if (!isNaN(credits) && credits > 0) {
      return credits;
    }
  }

  if (plan.metadata?.credit) {
    const credits = parseInt(plan.metadata.credit);
    if (!isNaN(credits) && credits > 0) {
      return credits;
    }
  }

  return 1000; // Default fallback
};

const isPayAsYouGoPlan = (plan: Plan): boolean => {
  if (plan.metadata?.type === "payg") {
    return true;
  }

  return false;
};

const getPerUnitPrice = (plan: Plan): number => {
  if (plan.default_price && plan.default_price.unit_amount_decimal) {
    return parseFloat(plan.default_price.unit_amount_decimal);
  }

  if (plan.price && plan.price > 0) {
    return plan.price;
    // const credits = getPlanCredits(plan);
    // return plan.price / credits;
  }

  return 0;
};

const getFeaturesList = (
  plan: any,
  isPayg: boolean,
  isFree: boolean
): string[] => {
  // First, try to get features from marketing_features
  if (plan.marketing_features && plan.marketing_features.length > 0) {
    return plan.marketing_features.map((feature: any) => feature.name);
  }

  // Then try regular features array
  if (plan.features && plan.features.length > 0) {
    return plan.features.map((feature: any) =>
      typeof feature === "string" ? feature : feature.name || "Feature"
    );
  }

  // Fallback features based on plan type
  if (isFree) {
    return ["Basic API access", "Limited rate limits", "Community support"];
  }

  if (isPayg) {
    return [
      "Pay per use",
      "No monthly commitment",
      "Standard rate limits",
      "Email support",
    ];
  }

  return [
    "Full API access",
    "Higher rate limits",
    "Priority support",
    "Advanced features",
  ];
};

export default function Plans({ plans, refetchCredits, refetchSubscription }: PlansProps) {
  const { toast } = useToast();
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);

  const handlePlanSelect = async (priceId: string, isFree: boolean = false) => {
    try {
      setProcessingPlanId(priceId);

      if (isFree) {
        const result = await apiClient.refillFreeCredits();

        if (result.alreadyClaimed) {
          toast({
            title: "Already claimed",
            description: result.message,
            variant: "default",
          });
        } else {
          toast({
            title: "Free credits refilled!",
            description:
              "You have successfully claimed this month's free credits.",
            variant: "default",
          });

          // Refresh subscription data to reflect the change
          refetchSubscription && (await refetchSubscription());
        }
        refetchCredits && (await refetchCredits());
      } else {
        // Handle paid plan checkout
        const { url } =
          await apiClient.createCheckoutSessionWithDefaults(priceId);
        window.location.href = url;
      }
    } catch (error) {
      console.error("Error selecting plan:", error);

      toast({
        title: "Failed to select plan",
        description:
          error instanceof Error
            ? error.message
            : "Unable to process your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPlanId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Available Plans
        </h2>
        <p className="text-gray-600">
          Choose the plan that best fits your needs
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const planCredits = getPlanCredits(plan);
          const isPayg = isPayAsYouGoPlan(plan);
          const isFree = (plan.price || 0) === 0;
          const isPopular =
            plan.name?.toLowerCase().includes("trader") ||
            plan.name?.toLowerCase().includes("project");
          const planColor = getPlanColor(plan.metadata);
          const cardStyle: CSSProperties = {
            borderColor: plan.current ? planColor : isPopular ? withAlpha(planColor, "66") : undefined,
            background: isPayg ? withAlpha(planColor, "0D") : undefined,
          };
          const badgeStyle: CSSProperties = { backgroundColor: planColor };
          const buttonStyle: CSSProperties = plan.current
            ? { borderColor: planColor, color: planColor }
            : { backgroundColor: planColor };

          return (
            <Card
              key={plan.id}
              style={cardStyle}
              className={`relative flex flex-col h-full border-2 bg-white ${plan.current || isPopular ? "shadow-md" : "border-gray-200"}`}
            >
              {/* Top badges */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                {plan.current ? (
                  <Badge style={badgeStyle} className="text-white px-3 py-1">
                    Current Plan
                  </Badge>
                ) : isPopular ? (
                  <Badge style={badgeStyle} className="text-white px-3 py-1">
                    Most Popular
                  </Badge>
                ) : isFree ? (
                  <Badge style={badgeStyle} className="text-white px-3 py-1">
                    Free
                  </Badge>
                ) : isPayg ? (
                  <Badge style={badgeStyle} className="text-white px-3 py-1">
                    Pay-as-you-go
                  </Badge>
                ) : null}
              </div>

              {/* Plan header */}
              <CardHeader className="text-center pt-8 pb-4">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </CardTitle>

                <CardDescription className="text-gray-600 mb-6 min-h-[3rem] flex items-center justify-center">
                  {plan.description || "Perfect for your needs"}
                </CardDescription>

                {/* Pricing */}
                <div className="space-y-2">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-bold text-gray-900">
                      {isFree
                        ? "$0"
                        : isPayg
                          ? formatCurrency(
                              getPerUnitPrice(plan),
                              plan.currency || "usd"
                            )
                          : formatCurrency(plan.price || 0, plan.currency)}
                    </span>
                    <span className="text-lg text-gray-500">
                      {isFree
                        ? ""
                        : isPayg
                          ? `/ ${formatNumber(planCredits)} credits`
                          : `/month`}
                    </span>
                  </div>
                </div>
              </CardHeader>

              {/* Features */}
              <CardContent className="flex-grow px-6 pb-6">
                <div className="space-y-3">
                  {getFeaturesList(plan, isPayg, isFree).map(
                    (feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Check style={{ color: planColor }} className="w-5 h-5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Additional info for subscription plans */}
                {isFree && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500 text-center">
                      No credit card required
                    </div>
                  </div>
                )}

                {!isFree && !isPayg && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500 text-center">
                      Annual:{" "}
                      {formatCurrency(
                        (plan.price || 0) * 12 * 0.8,
                        plan.currency || "usd"
                      )}
                      /year (Save{" "}
                      {formatCurrency(
                        (plan.price || 0) * 12 * 0.2,
                        plan.currency || "usd"
                      )}
                      )
                    </div>
                  </div>
                )}

                {/* No monthly commitment for PAYG */}
                {isPayg && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500 text-center">
                      No monthly commitment
                    </div>
                  </div>
                )}
              </CardContent>

              {/* Action button */}
              <div className="p-6 pt-0">
                <Button
                  style={buttonStyle}
                  className="w-full py-3 text-base font-semibold text-white hover:opacity-90"
                  variant={plan.current ? "outline" : "default"}
                  disabled={
                    plan.current ||
                    !plan.active ||
                    processingPlanId === plan.priceId
                  }
                  onClick={() => handlePlanSelect(plan.priceId, isFree)}
                >
                  {processingPlanId === plan.priceId ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </div>
                  ) : plan.current ? (
                    "Current Plan"
                  ) : !plan.active ? (
                    "Unavailable"
                  ) : isFree ? (
                    "Sign Up for Free"
                  ) : isPayg ? (
                    "Buy Credits"
                  ) : isPopular ? (
                    "Start Trader Plan"
                  ) : (
                    "Select Plan"
                  )}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
